import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { ShoppingCart, Wrench, Package } from "lucide-react";
import { OrderDetailData } from "@/types/orders";
import { formatCurrency } from "@/config/order-constants";
import { ConfigComponentsSummary } from "./ConfigComponentsSummary";

// Type for order line with configuration from Supabase query
type OrderLineWithConfig = OrderDetailData["order_lines"][number];

interface OrderLinesCardProps {
  order: OrderDetailData;
}

export function OrderLinesCard({ order }: OrderLinesCardProps) {
  const orderLines = order.order_lines || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5" />
          Tilausrivit
        </CardTitle>
      </CardHeader>
      <CardContent>
        {orderLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ei tilausrivejä</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tuote</TableHead>
                  <TableHead className="w-[80px] text-center">Määrä</TableHead>
                  <TableHead className="w-[120px] text-right">Yksikköhinta</TableHead>
                  <TableHead className="w-[120px] text-right">Yhteensä</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderLines.map((line: OrderLineWithConfig) => {
                  const lineConfig = line.configurations;
                  const isCustomTemplate = lineConfig?.product_models?.is_custom_template;
                  const configComponents = lineConfig?.config_components || [];
                  
                  return (
                    <TableRow key={line.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          {lineConfig ? (
                            <>
                              <span className="font-medium">
                                {lineConfig.name || line.description}
                              </span>
                              {lineConfig.product_models?.name && (
                                <>
                                  <span className={`text-xs flex items-center gap-1 ${isCustomTemplate ? "text-amber-600" : "text-blue-600"}`}>
                                    {isCustomTemplate ? <Wrench className="h-3 w-3" /> : <Package className="h-3 w-3" />}
                                    {lineConfig.product_models.name}
                                  </span>
                                  <ConfigComponentsSummary components={configComponents} />
                                </>
                              )}
                            </>
                          ) : (
                            <span className="font-medium">{line.description}</span>
                          )}
                          {line.products?.sku && (
                            <span className="text-xs text-muted-foreground">
                              {line.products.sku}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{line.quantity}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(line.unit_price)}
                        {line.price_includes_vat && (
                          <span className="text-xs text-muted-foreground ml-1">
                            (sis. ALV)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(line.line_total)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Separator className="my-4" />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Välisumma (ALV 0%)</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">ALV</span>
                <span>{formatCurrency(order.vat_amount)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold text-lg">
                <span>Yhteensä</span>
                <span>{formatCurrency(order.total_amount)}</span>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
